import { create } from "zustand";
import type {
  AgentStatus,
  ChatMessage,
  ToolCallEvent,
  ToolResultEvent,
  EditEvent,
} from "../types/ai";
import { normalizeEditEvent } from "../types/ai";
import { api } from "../lib/api";
import { useAIStore } from "./ai";

interface ChatState {
  messages: ChatMessage[];
  agentStatus: AgentStatus;
  streamingContent: string;
  streamingThinking: string;
  pendingEdits: EditEvent[];
  isLoading: boolean;
  _abortController: AbortController | null;

  fetchHistory: (assignmentId: string) => Promise<void>;
  sendMessageStream: (params: {
    assignmentId: string;
    content: string;
    documentId?: string;
    referencedFileIds?: string[];
    cursorLine?: number;
    selectionStart?: number;
    selectionEnd?: number;
    selectedText?: string;
    onEdit?: (edit: EditEvent) => void;
  }) => Promise<void>;
  cancelStream: () => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>()((set, get) => ({
  messages: [],
  agentStatus: "idle",
  streamingContent: "",
  streamingThinking: "",
  pendingEdits: [],
  isLoading: false,
  _abortController: null,

  fetchHistory: async (assignmentId) => {
    set({ isLoading: true });
    try {
      const raw = await api.get<Array<Record<string, unknown>>>(
        `/api/v1/assignments/${assignmentId}/chat`,
      );
      const messages: ChatMessage[] = raw.map((m) => {
        const meta = m.metadata_json as Record<string, unknown> | undefined;
        const rawEdits = meta?.edits as Array<Record<string, unknown>> | undefined;
        return {
          id: (m.id as string) || `hist-${Math.random()}`,
          role: (m.role as "user" | "assistant") || "user",
          content: (m.content as string) || "",
          edits: rawEdits?.map((e) => normalizeEditEvent(e)),
          timestamp: new Date((m.created_at as string) || "").getTime(),
        };
      });
      set({ messages });
    } catch {
      // Chat history unavailable
    } finally {
      set({ isLoading: false });
    }
  },

  cancelStream: () => {
    const { _abortController } = get();
    if (_abortController) {
      _abortController.abort();
      set({ _abortController: null, agentStatus: "idle", streamingContent: "", streamingThinking: "" });
    }
  },

  sendMessageStream: async (params) => {
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: params.content,
      timestamp: Date.now(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      agentStatus: "thinking",
      streamingContent: "",
      pendingEdits: [],
    }));

    const abortController = new AbortController();
    set({ _abortController: abortController });

    let receivedDone = false;
    let fullContent = "";
    let fullThinking = "";

    try {
      let lastToolCallId: string | undefined;
      const toolCalls: ToolCallEvent[] = [];
      const toolResults: ToolResultEvent[] = [];
      const edits: EditEvent[] = [];

      const { selectedProvider, selectedModel } = useAIStore.getState();
      const payload = {
        content: params.content,
        document_id: params.documentId,
        referenced_file_ids: params.referencedFileIds,
        provider: selectedProvider || undefined,
        model: selectedModel || undefined,
        cursor_line: params.cursorLine,
        selection_start: params.selectionStart,
        selection_end: params.selectionEnd,
        selected_text: params.selectedText,
      };

      for await (const data of api.stream(
        `/api/v1/assignments/${params.assignmentId}/chat/stream`,
        payload,
        abortController.signal,
      )) {
        try {
          const event = JSON.parse(data);

          if (event.event === "thinking") {
            const chunk = event.data?.content || "";
            fullThinking = fullThinking ? `${fullThinking}\n\n${chunk}` : chunk;
            set({ streamingThinking: fullThinking });
          }

          if (event.event === "status") {
            const statusMap: Record<string, AgentStatus> = {
              thinking: "thinking",
              reading: "reading",
              compiling: "compiling",
              editing: "editing",
            };
            set({ agentStatus: statusMap[event.data?.status] || "thinking" });
          }

          if (event.event === "text") {
            fullContent += event.data?.content || "";
            set({ streamingContent: fullContent });
          }

          if (event.event === "tool_call") {
            lastToolCallId = event.data?.id as string | undefined;
            toolCalls.push(event.data as ToolCallEvent);
            const toolStatusMap: Record<string, AgentStatus> = {
              compile_latex: "compiling",
              edit_document: "editing",
              edit_lines: "editing",
              read_document: "reading",
              read_section: "reading",
              search_document: "reading",
            };
            set({ agentStatus: toolStatusMap[event.data?.tool] || "thinking" });
          }

          if (event.event === "tool_result") {
            toolResults.push(event.data as ToolResultEvent);
          }

          if (event.event === "edit") {
            const edit = normalizeEditEvent(
              event.data as Record<string, unknown>,
              lastToolCallId,
            );
            edits.push(edit);
            set((s) => ({ pendingEdits: [...s.pendingEdits, edit] }));
            params.onEdit?.(edit);
          }

          if (event.event === "error") {
            fullContent += event.data?.message || "Something went wrong.";
            set({ streamingContent: fullContent });
          }

          if (event.event === "done") {
            receivedDone = true;
            set({ agentStatus: "done" });
            setTimeout(() => {
              set((s) => (s.agentStatus === "done" ? { agentStatus: "idle" } : {}));
            }, 2000);
          }
        } catch {
          // Skip malformed JSON
        }
      }

      if (!receivedDone) {
        set({ agentStatus: "idle" });
      }

      const replyText = fullContent.trim()
        ? fullContent
        : !receivedDone
          ? "No response received. The connection may have dropped — try sending your message again."
          : "";

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: replyText,
        thinking: fullThinking || undefined,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        toolResults: toolResults.length > 0 ? toolResults : undefined,
        edits: edits.length > 0 ? edits : undefined,
        timestamp: Date.now(),
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        streamingContent: "",
        streamingThinking: "",
        _abortController: null,
      }));
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        set({ _abortController: null, agentStatus: "idle", streamingContent: "", streamingThinking: "" });
        return;
      }
      set({ agentStatus: "idle", streamingContent: "", streamingThinking: "", _abortController: null });
      const errorMsg: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "Sorry, something went wrong. Please try again.",
        timestamp: Date.now(),
      };
      set((s) => ({ messages: [...s.messages, errorMsg] }));
    }
  },

  clearChat: () => set({ messages: [], streamingContent: "", streamingThinking: "", pendingEdits: [] }),
}));
